import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';

import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { Dialog } from 'primeng/dialog';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { forkJoin } from 'rxjs';

import { TimesheetService } from '../../services/timesheet.service';
import { EmployeeService } from '../../services/employee.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { AllocationService } from '../../services/allocation.service';
import { ProjectService } from '../../services/project.service';
import { AuthService } from '../../services/auth.service';
import { PersistColumnWidthsDirective } from '../../shared/directives/persist-column-widths.directive';
import { Timesheet, TimesheetEntry } from '../../models/timesheet.model';
import { Allocation } from '../../models/allocation.model';
import { Project } from '../../models/project.model';
import { Employee } from '../../models/employee.model';
import { ServiceGroup } from '../../models/service-group.model';
import { Month } from '../../models/allocation.model';
import { convertWeekHoursToDecimal as convertWeekHoursToDecimalUtil, formatHoursMinutes as formatHoursMinutesUtil, formatTotalHours as formatTotalHoursUtil, formatHoursMinutesFromHM, formatTotalHoursFromDecimalArray, parseHoursMinutes, sumHoursMinutes, getTimesheetTotalHoursMinutes, hoursMinutesToDecimalHours as hoursMinutesToDecimalHoursUtil, entryWeeklyHours } from '../../utils/timesheet-hours.util';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface TimesheetReportRow {
  employeeId: string;
  employeeName: string;
  week1Hours: number;
  week2Hours: number;
  week3Hours: number;
  week4Hours: number;
  week5Hours: number;
  totalHours: number;
  isSubmitted: boolean;
  isApproved: boolean;
  timesheet: Timesheet;
  /** Project-wise breakdown for expanded view (includes PM). */
  expandedProjectRows: ProjectSummaryRow[];
  /** True if any assigned project has zero hours (highlight main row in red). */
  hasAnyZeroHoursProject: boolean;
}

export interface ProjectSummaryRow {
  projectId: string;
  projectName: string;
  pmName: string;
  week1Hours: number;
  week2Hours: number;
  week3Hours: number;
  week4Hours: number;
  week5Hours: number;
  totalHours: number;
}

@Component({
  selector: 'app-timesheet-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Select,
    MultiSelect,
    Dialog,
    Tag,
    Tooltip,
    ConfirmDialog,
    PersistColumnWidthsDirective
  ],
  templateUrl: './timesheet-report.component.html',
  styleUrl: './timesheet-report.component.scss',
  providers: [ConfirmationService]
})
export class TimesheetReportComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  loading = false;

  selectedMonth: Month = (new Date().getMonth() + 1) as Month;
  selectedYear: number = new Date().getFullYear();
  selectedFromMonth: Month = this.selectedMonth;
  selectedFromYear: number = this.selectedYear;
  selectedToMonth: Month = this.selectedMonth;
  selectedToYear: number = this.selectedYear;
  periodModeOptions: { label: string; value: 'single' | 'range' }[] = [
    { label: 'Single Month', value: 'single' },
    { label: 'Custom Range', value: 'range' }
  ];
  selectedPeriodMode: 'single' | 'range' = 'single';

  months = [
    { label: 'January', value: Month.January },
    { label: 'February', value: Month.February },
    { label: 'March', value: Month.March },
    { label: 'April', value: Month.April },
    { label: 'May', value: Month.May },
    { label: 'June', value: Month.June },
    { label: 'July', value: Month.July },
    { label: 'August', value: Month.August },
    { label: 'September', value: Month.September },
    { label: 'October', value: Month.October },
    { label: 'November', value: Month.November },
    { label: 'December', value: Month.December }
  ];

  years: { label: string; value: number }[] = [];

  filterEmployeeName = '';

  statusOptions: { label: string; value: string }[] = [
    { label: 'Draft', value: 'draft' },
    { label: 'Submitted', value: 'submitted' },
    { label: 'Approved', value: 'approved' },
    { label: 'Not Entered', value: 'notEntered' }
  ];
  selectedStatuses: string[] = [];

  totalHoursFilterOptions: { label: string; value: string }[] = [
    { label: 'Less than 120 hours', value: 'lt120' },
    { label: '120 – 144 hours', value: '120-144' },
    { label: '145 – 160 hours', value: '145-160' },
    { label: '161 – 176 hours', value: '161-176' },
    { label: 'More than 176 hours', value: 'gt176' }
  ];
  selectedTotalHoursThresholds: string[] = [];

  employees: Employee[] = [];
  /** Employee id -> list of service group ids (supports multiple service groups per employee). */
  employeeIdToServiceGroupIds = new Map<string, string[]>();
  serviceGroups: ServiceGroup[] = [];
  serviceGroupOptions: { label: string; value: string }[] = [];
  selectedServiceGroupIds: string[] = [];
  projectFilterOptions: { label: string; value: string }[] = [];
  selectedProjectIds: string[] = [];

  allRows: TimesheetReportRow[] = [];
  rows: TimesheetReportRow[] = [];

  showTimesheetSummaryDialog = false;

  showSummaryDialog = false;
  selectedEmployeeRow: TimesheetReportRow | null = null;
  projectSummary: ProjectSummaryRow[] = [];

  /** projectId -> PM name for project-wise display */
  private projectIdToPm = new Map<string, string>();
  /** Project IDs owned by the logged-in PM (empty = not a PM or admin) */
  private pmProjectIds = new Set<string>();
  private pmOwnProjectIds = new Set<string>(); // non-shared projects only – used to filter employee visibility

  constructor(
    private timesheetService: TimesheetService,
    private employeeService: EmployeeService,
    private serviceGroupService: ServiceGroupService,
    private allocationService: AllocationService,
    private projectService: ProjectService,
    private authService: AuthService,
    private router: Router,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  ngOnInit(): void {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search employees...');
    this.pageHeaderService.setPrimaryButtonLabel('Summary');
    this.pageHeaderService.setPrimaryButtonIcon('pi pi-chart-bar');
    this.pageHeaderService.setCanShowPrimaryAction(true);
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(false);
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(text => {
      this.filterEmployeeName = text;
      this.applyFilters();
    });
    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.openTimesheetSummaryDialog();
    });
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.exportReportSummaryToExcel();
    });

    const currentYear = new Date().getFullYear();
    for (let y = currentYear - 2; y <= currentYear + 2; y++) {
      this.years.push({ label: y.toString(), value: y });
    }
    this.employeeService.getAllEmployees().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list) => {
        this.employees = (list || []).filter((e) => e.isActive && !e.excludeFromTimesheet);
        this.employeeIdToServiceGroupIds = new Map(
          this.employees
            .filter((e) => (e.serviceGroupIds?.length ?? 0) > 0 || e.serviceGroupId)
            .map((e) => [
              e.id,
              (e.serviceGroupIds && e.serviceGroupIds.length > 0) ? e.serviceGroupIds : [e.serviceGroupId!]
            ])
        );
        this.loadReport();
      },
      error: () => {
        this.employees = [];
        this.loadReport();
      }
    });
    this.serviceGroupService.getAllServiceGroups().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list) => {
        this.serviceGroups = (list || []).filter((sg) => sg.isActive);
        this.serviceGroupOptions = this.serviceGroups.map((sg) => ({ label: sg.name, value: sg.id }));
      },
      error: () => (this.serviceGroups = [])
    });
  }

  onMonthYearChange(): void {
    if (this.isCustomRangeActive) return;
    this.loadReport();
  }

  onPeriodModeChange(): void {
    if (!this.canUseCustomRange) {
      this.selectedPeriodMode = 'single';
    }
    if (!this.isCustomRangeActive) {
      this.selectedMonth = this.selectedToMonth;
      this.selectedYear = this.selectedToYear;
    }
    this.loadReport();
  }

  onRangeChange(): void {
    if (!this.isCustomRangeActive) return;
    this.normalizeRange();
    this.loadReport();
  }

  onEmployeeFilterChange(): void {
    this.applyFilters();
  }

  onServiceGroupFilterChange(): void {
    this.applyFilters();
  }

  onProjectFilterChange(): void {
    this.applyFilters();
  }

  onStatusFilterChange(): void {
    this.applyFilters();
  }

  onTotalHoursFilterChange(): void {
    this.applyFilters();
  }

  /** True if any filter is applied (for showing clear icon). */
  hasActiveFilters(): boolean {
    const hasSearch = (this.filterEmployeeName ?? '').trim().length > 0;
    const hasServiceGroup = (this.selectedServiceGroupIds?.length ?? 0) > 0;
    const hasProject = (this.selectedProjectIds?.length ?? 0) > 0;
    const hasStatus = (this.selectedStatuses?.length ?? 0) > 0;
    const hasTotalHours = (this.selectedTotalHoursThresholds?.length ?? 0) > 0;
    return hasSearch || hasServiceGroup || hasProject || hasStatus || hasTotalHours;
  }

  clearFilters(): void {
    this.filterEmployeeName = '';
    this.selectedServiceGroupIds = [];
    this.selectedProjectIds = [];
    this.selectedStatuses = [];
    this.selectedTotalHoursThresholds = [];
    this.applyFilters();
  }

  get canUseCustomRange(): boolean {
    const user = this.authService.getUser();
    return !!(user?.isAdmin || user?.isPM || user?.isFinance);
  }

  get isCustomRangeActive(): boolean {
    return this.canUseCustomRange && this.selectedPeriodMode === 'range';
  }

  get canUseEmployeeRowActions(): boolean {
    return !this.isCustomRangeActive;
  }

  openTimesheetSummaryDialog(): void {
    this.showTimesheetSummaryDialog = true;
  }

  closeTimesheetSummaryDialog(): void {
    this.showTimesheetSummaryDialog = false;
  }

  /** Summary counts from full month data (allRows). Not affected by table filters. */
  get summaryTableRows(): { label: string; count: number }[] {
    const total = this.allRows.length;
    const approved = this.allRows.filter((r) => r.isApproved).length;
    const submitted = this.allRows.filter((r) => r.isSubmitted && !r.isApproved).length;
    const draft = this.allRows.filter((r) => !r.isSubmitted && !r.isApproved && !!r.timesheet?.id).length;
    const notEntered = this.allRows.filter((r) => !r.timesheet?.id || r.timesheet?.id === '').length;
    return [
      { label: 'Total No of Timesheets', count: total },
      { label: 'Total No of Approved Timesheets', count: approved },
      { label: 'Total No of Submitted Timesheets', count: submitted },
      { label: 'Total No of Draft Timesheets', count: draft },
      { label: 'Total No of Not Entered Timesheets', count: notEntered }
    ];
  }

  applyFilters(): void {
    let filtered = this.allRows;

    if (this.selectedServiceGroupIds?.length > 0) {
      filtered = filtered.filter((r) => {
        const sgIds = this.employeeIdToServiceGroupIds.get(r.employeeId) ?? [];
        return sgIds.some((sgId) => this.selectedServiceGroupIds!.includes(sgId));
      });
    }

    if (this.selectedProjectIds?.length > 0) {
      const selected = new Set(this.selectedProjectIds);
      filtered = filtered.filter((r) =>
        (r.expandedProjectRows ?? []).some((p) => selected.has(p.projectId))
      );
    }

    const searchTerm = this.filterEmployeeName.trim().toLowerCase() ?? '';
    if (searchTerm) {
      filtered = filtered.filter((r) => {
        const matchEmployee = (r.employeeName ?? '').toLowerCase().includes(searchTerm);
        const matchProject = (r.expandedProjectRows ?? []).some((p) =>
          (p.projectName ?? '').toLowerCase().includes(searchTerm)
        );
        return matchEmployee || matchProject;
      });
    }

    if (this.selectedStatuses?.length > 0) {
      filtered = filtered.filter((r) => {
        const rowStatus = this.getRowStatusKey(r);
        return this.selectedStatuses.includes(rowStatus);
      });
    }

    if (this.selectedTotalHoursThresholds?.length > 0) {
      filtered = filtered.filter(r => {
        const h = r.totalHours || 0;
        return this.selectedTotalHoursThresholds.some(range => {
          if (range === 'lt120') return h < 120;
          if (range === '120-144') return h >= 120 && h < 145;
          if (range === '145-160') return h >= 145 && h < 161;
          if (range === '161-176') return h >= 161 && h <= 176;
          if (range === 'gt176') return h > 176;
          return false;
        });
      });
    }

    this.rows = filtered;
  }

  loadReport(): void {
    this.loading = true;
    this.projectService.getAllProjects().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (projects) => {
        this.projectIdToPm = new Map((projects || []).map((p) => [p.id, p.managerName || '-']));

        // For PM: build set of own project IDs using managerId (reliable) with name fallback
        // Also include shared/holiday projects (isAvailableToAllEmployees) so PM can see and approve them
        const user = this.authService.getUser();
        this.pmProjectIds = new Set<string>();
        this.pmOwnProjectIds = new Set<string>();
        if (user && !user.isAdmin && user.isPM) {
          const pmEmployee = this.employees.find(
            e => e.email?.toLowerCase() === user.email?.toLowerCase()
          );
          if (pmEmployee) {
            projects.filter(p => p.managerId === pmEmployee.id).forEach(p => {
              this.pmProjectIds.add(p.id);
              this.pmOwnProjectIds.add(p.id);
            });
          } else {
            const pmNameLower = (user.displayName ?? '').toLowerCase().trim();
            projects.filter(p => (p.managerName ?? '').toLowerCase().trim() === pmNameLower)
              .forEach(p => {
                this.pmProjectIds.add(p.id);
                this.pmOwnProjectIds.add(p.id);
              });
          }
          // Shared projects (Holidays & Leave, Internal Activities) go into pmProjectIds only,
          // NOT pmOwnProjectIds — so they don't count towards "has assigned project" check
          projects.filter(p => p.isAvailableToAllEmployees).forEach(p => this.pmProjectIds.add(p.id));
        }

        this.loadReportWithProjectsAndAllocations();
      },
      error: () => {
        this.projectIdToPm = new Map();
        this.pmProjectIds = new Set<string>();
        this.pmOwnProjectIds = new Set<string>();
        this.loadReportWithProjectsAndAllocations();
      }
    });
  }

  private loadReportWithProjectsAndAllocations(): void {
    const periods = this.getSelectedPeriods();
    const allocationRequests = periods.map((p) =>
      this.allocationService.getAllAllocations(p.year, p.month)
    );
    const timesheetRequests = periods.map((p) =>
      this.timesheetService.getTimesheetsByMonthYear(p.year, p.month)
    );

    forkJoin(allocationRequests).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (allocationsByPeriod) => {
        const allocations = allocationsByPeriod.flatMap((x) => x || []);
        const allocationsByEmployee = this.groupAllocationsByEmployee(allocations);
        forkJoin(timesheetRequests).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (timesheetsByPeriod) => {
            const timesheets = timesheetsByPeriod.flatMap((x) => x || []);
            this.rebuildRows(timesheets, allocationsByEmployee);
          },
          error: (error) => this.handleLoadReportError(error)
        });
      },
      error: () => {
        forkJoin(timesheetRequests).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (timesheetsByPeriod) => {
            const timesheets = timesheetsByPeriod.flatMap((x) => x || []);
            this.rebuildRows(timesheets, new Map());
          },
          error: (err) => this.handleLoadReportError(err)
        });
      }
    });
  }

  private rebuildRows(
    timesheets: Timesheet[],
    allocationsByEmployee: Map<string, { projectId: string; projectName: string; pmName: string }[]>
  ): void {
    // PM: only show employees who have allocations on PM's own (non-shared) projects.
    let pmEmployeeIds: Set<string> | null = null;
    if (this.pmProjectIds.size > 0) {
      if (this.pmOwnProjectIds.size > 0) {
        pmEmployeeIds = new Set(
          [...allocationsByEmployee.entries()]
            .filter(([, projs]) => projs.some((p) => this.pmOwnProjectIds.has(p.projectId)))
            .map(([empId]) => empId)
        );
      } else {
        pmEmployeeIds = new Set<string>();
      }
    }

    const activeEmployeeIds = new Set(this.employees.map((e) => e.id));
    const timesheetsByEmployee = new Map<string, Timesheet[]>();
    for (const t of timesheets || []) {
      if (!activeEmployeeIds.has(t.employeeId)) continue;
      if (pmEmployeeIds && !pmEmployeeIds.has(t.employeeId)) continue;
      if (!timesheetsByEmployee.has(t.employeeId)) timesheetsByEmployee.set(t.employeeId, []);
      timesheetsByEmployee.get(t.employeeId)!.push(t);
    }

    const rows: TimesheetReportRow[] = [];
    for (const [employeeId, employeeTimesheets] of timesheetsByEmployee.entries()) {
      const merged = this.mergeEmployeeTimesheets(employeeId, employeeTimesheets);
      rows.push(this.buildReportRow(merged, allocationsByEmployee.get(employeeId) || []));
    }

    const withTimesheetIds = new Set(rows.map((r) => r.employeeId));
    for (const emp of this.employees) {
      if (!withTimesheetIds.has(emp.id)) {
        if (pmEmployeeIds && !pmEmployeeIds.has(emp.id)) continue;
        const placeholderTimesheet: Timesheet = {
          id: '',
          employeeId: emp.id,
          employeeName: emp.name,
          month: this.selectedMonth,
          year: this.selectedYear,
          isSubmitted: false,
          isApproved: false,
          entries: []
        };
        rows.push(this.buildReportRow(placeholderTimesheet, allocationsByEmployee.get(emp.id) || []));
      }
    }

    this.allRows = rows.sort((a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''));
    this.updateProjectFilterOptions();
    this.applyFilters();
    this.loading = false;
  }

  private updateProjectFilterOptions(): void {
    const uniqueProjects = new Map<string, string>();
    for (const row of this.allRows) {
      for (const project of row.expandedProjectRows || []) {
        if (project.projectId && !uniqueProjects.has(project.projectId)) {
          uniqueProjects.set(project.projectId, project.projectName || '-');
        }
      }
    }

    this.projectFilterOptions = [...uniqueProjects.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));

    if ((this.selectedProjectIds?.length ?? 0) > 0) {
      const valid = new Set(this.projectFilterOptions.map((p) => p.value));
      this.selectedProjectIds = this.selectedProjectIds.filter((id) => valid.has(id));
    }
  }

  private mergeEmployeeTimesheets(employeeId: string, timesheets: Timesheet[]): Timesheet {
    const employee = this.employees.find((e) => e.id === employeeId);
    const allEntries = timesheets.flatMap((t) => t.entries || []);
    const hasTimesheet = timesheets.some((t) => !!t.id);
    const allApproved = timesheets.length > 0 && timesheets.every((t) => !!t.isApproved);
    const allSubmittedOrApproved = timesheets.length > 0 && timesheets.every((t) => !!t.isSubmitted || !!t.isApproved);
    const isSubmitted = !allApproved && allSubmittedOrApproved;
    const base = timesheets[0];

    return {
      id: this.isCustomRangeActive ? (hasTimesheet ? 'range-aggregate' : '') : (base?.id || ''),
      employeeId,
      employeeName: base?.employeeName || employee?.name || '',
      month: base?.month ?? this.selectedMonth,
      year: base?.year ?? this.selectedYear,
      isSubmitted,
      isApproved: allApproved,
      entries: allEntries
    };
  }

  private normalizeRange(): void {
    const from = this.selectedFromYear * 100 + this.selectedFromMonth;
    const to = this.selectedToYear * 100 + this.selectedToMonth;
    if (from <= to) return;
    const oldFromMonth = this.selectedFromMonth;
    const oldFromYear = this.selectedFromYear;
    this.selectedFromMonth = this.selectedToMonth;
    this.selectedFromYear = this.selectedToYear;
    this.selectedToMonth = oldFromMonth;
    this.selectedToYear = oldFromYear;
  }

  private getSelectedPeriods(): { year: number; month: Month }[] {
    if (!this.isCustomRangeActive) {
      return [{ year: this.selectedYear, month: this.selectedMonth }];
    }

    this.normalizeRange();
    const periods: { year: number; month: Month }[] = [];
    let year = this.selectedFromYear;
    let month = this.selectedFromMonth;
    const maxPeriods = 36;

    while ((year < this.selectedToYear || (year === this.selectedToYear && month <= this.selectedToMonth)) && periods.length < maxPeriods) {
      periods.push({ year, month });
      month = (month + 1) as Month;
      if (month > Month.December) {
        month = Month.January;
        year += 1;
      }
    }

    if (periods.length === maxPeriods) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Range trimmed',
        detail: 'Maximum 36 months can be loaded at once.'
      });
    }
    return periods;
  }

  private groupAllocationsByEmployee(allocations: Allocation[]): Map<string, { projectId: string; projectName: string; pmName: string }[]> {
    // PM: only include allocations for their own projects
    const filtered = this.pmProjectIds.size > 0
      ? allocations.filter(a => this.pmProjectIds.has(a.projectId))
      : allocations;
    const map = new Map<string, { projectId: string; projectName: string; pmName: string }[]>();
    for (const a of filtered) {
      if (!map.has(a.employeeId)) map.set(a.employeeId, []);
      const list = map.get(a.employeeId)!;
      if (!list.some((x) => x.projectId === a.projectId)) {
        list.push({
          projectId: a.projectId,
          projectName: a.projectName || '-',
          pmName: a.managerName || this.projectIdToPm.get(a.projectId) || '-'
        });
      }
    }
    return map;
  }

  private buildReportRow(
    timesheet: Timesheet,
    assignedProjects: { projectId: string; projectName: string; pmName: string }[]
  ): TimesheetReportRow {
    const entries = timesheet.entries || [];
    // Via entryWeeklyHours, not the weekN columns directly: date-mode entries (the default in the
    // Work Log screen) store their hours in `hours` + `date` and leave weekN at 0, so reading those
    // columns alone reported 0h and "Not Entered" for anyone who logged time that way.
    const weekly = entries.map((e) => entryWeeklyHours(e));
    const w1 = sumHoursMinutes(weekly.map((w) => w[0]));
    const w2 = sumHoursMinutes(weekly.map((w) => w[1]));
    const w3 = sumHoursMinutes(weekly.map((w) => w[2]));
    const w4 = sumHoursMinutes(weekly.map((w) => w[3]));
    const w5 = sumHoursMinutes(weekly.map((w) => w[4]));
    const totalHM = sumHoursMinutes(weekly.flat());
    const week1Hours = w1.h + w1.m / 60;
    const week2Hours = w2.h + w2.m / 60;
    const week3Hours = w3.h + w3.m / 60;
    const week4Hours = w4.h + w4.m / 60;
    const week5Hours = w5.h + w5.m / 60;
    const totalHours = totalHM.h + totalHM.m / 60;

    const allExpandedRows = this.buildExpandedProjectRows(timesheet, assignedProjects);
    // PM: only show project rows for their own (non-shared) projects in the expanded view.
    // Shared projects (Holidays & Leave, Internal Activities) are excluded from PM's expanded view.
    const expandedProjectRows = this.pmProjectIds.size > 0
      ? allExpandedRows.filter(r => this.pmOwnProjectIds.has(r.projectId))
      : allExpandedRows;
    const hasAnyZeroHoursProject = expandedProjectRows.some((p) => p.totalHours == null || p.totalHours < 1e-6);

    return {
      employeeId: timesheet.employeeId,
      employeeName: timesheet.employeeName ?? '',
      week1Hours,
      week2Hours,
      week3Hours,
      week4Hours,
      week5Hours,
      totalHours,
      isSubmitted: !!timesheet.isSubmitted,
      isApproved: !!timesheet.isApproved,
      timesheet,
      expandedProjectRows,
      hasAnyZeroHoursProject
    };
  }

  private buildExpandedProjectRows(
    timesheet: Timesheet,
    assignedProjects: { projectId: string; projectName: string; pmName: string }[]
  ): ProjectSummaryRow[] {
    const entries = timesheet.entries || [];
    const byProject = new Map<string, TimesheetEntry[]>();
    for (const e of entries) {
      const key = e.projectId || e.projectName || 'unknown';
      if (!byProject.has(key)) byProject.set(key, []);
      byProject.get(key)!.push(e);
    }

    const hoursByProject = new Map<string, { w1: number; w2: number; w3: number; w4: number; w5: number }>();
    for (const [projectKey, projectEntries] of byProject.entries()) {
      // Same date-vs-week storage split as buildReportRow — see entryWeeklyHours.
      const weekly = projectEntries.map((e) => entryWeeklyHours(e as any));
      const w1 = sumHoursMinutes(weekly.map((w) => w[0]));
      const w2 = sumHoursMinutes(weekly.map((w) => w[1]));
      const w3 = sumHoursMinutes(weekly.map((w) => w[2]));
      const w4 = sumHoursMinutes(weekly.map((w) => w[3]));
      const w5 = sumHoursMinutes(weekly.map((w) => w[4]));
      hoursByProject.set(projectKey, {
        w1: w1.h + w1.m / 60,
        w2: w2.h + w2.m / 60,
        w3: w3.h + w3.m / 60,
        w4: w4.h + w4.m / 60,
        w5: w5.h + w5.m / 60
      });
    }

    const projectIdToInfo = new Map<string, { projectName: string; pmName: string }>();
    for (const ap of assignedProjects) {
      projectIdToInfo.set(ap.projectId, { projectName: ap.projectName, pmName: ap.pmName });
    }
    for (const e of entries) {
      const id = e.projectId || e.projectName;
      if (id && !projectIdToInfo.has(id)) {
        projectIdToInfo.set(id, {
          projectName: e.projectName || '-',
          pmName: this.projectIdToPm.get(e.projectId) || '-'
        });
      }
    }

    const result: ProjectSummaryRow[] = [];
    const seenIds = new Set<string>();
    for (const ap of assignedProjects) {
      if (seenIds.has(ap.projectId)) continue;
      seenIds.add(ap.projectId);
      const hours = hoursByProject.get(ap.projectId) || { w1: 0, w2: 0, w3: 0, w4: 0, w5: 0 };
      const total = hours.w1 + hours.w2 + hours.w3 + hours.w4 + hours.w5;
      result.push({
        projectId: ap.projectId,
        projectName: ap.projectName,
        pmName: ap.pmName,
        week1Hours: hours.w1,
        week2Hours: hours.w2,
        week3Hours: hours.w3,
        week4Hours: hours.w4,
        week5Hours: hours.w5,
        totalHours: total
      });
    }
    for (const [projectKey, hours] of hoursByProject.entries()) {
      if (seenIds.has(projectKey)) continue;
      const info = projectIdToInfo.get(projectKey) || { projectName: projectKey, pmName: this.projectIdToPm.get(projectKey) || '-' };
      const total = hours.w1 + hours.w2 + hours.w3 + hours.w4 + hours.w5;
      result.push({
        projectId: projectKey,
        projectName: info.projectName,
        pmName: info.pmName,
        week1Hours: hours.w1,
        week2Hours: hours.w2,
        week3Hours: hours.w3,
        week4Hours: hours.w4,
        week5Hours: hours.w5,
        totalHours: total
      });
    }
    return result.sort((a, b) => b.totalHours - a.totalHours);
  }

  private handleLoadReportError(error: unknown): void {
    console.error('Error loading timesheet report:', error);
    this.allRows = [];
    this.rows = [];
    this.projectFilterOptions = [];
    this.selectedProjectIds = [];
    this.loading = false;
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load timesheet report'
    });
  }

  openSummary(row: TimesheetReportRow): void {
    this.selectedEmployeeRow = row;
    this.projectSummary = row.expandedProjectRows || [];
    this.showSummaryDialog = true;
  }

  /** True if any assigned project has zero hours (highlight main row in red). */
  hasAnyZeroHoursProject(row: TimesheetReportRow): boolean {
    return !!row.hasAnyZeroHoursProject;
  }

  /** True if project row has zero hours (highlight in red when expanded). */
  isProjectZeroHours(projectRow: ProjectSummaryRow): boolean {
    return projectRow.totalHours == null || projectRow.totalHours < 1e-6;
  }

  closeSummary(): void {
    this.showSummaryDialog = false;
    this.selectedEmployeeRow = null;
    this.projectSummary = [];
  }

  get canApproveTimesheet(): boolean {
    const user = this.authService.getUser();
    return !!(user?.isAdmin || user?.isPM);
  }

  sendTimesheetReminder(row: TimesheetReportRow): void {
    this.confirmationService.confirm({
      message: `Send a timesheet reminder email to "${row.employeeName}"?`,
      header: 'Send Reminder',
      icon: 'pi pi-envelope',
      acceptButtonStyleClass: 'p-button-primary',
      accept: () => {
        this.timesheetService.sendTimesheetReminder(row.employeeId, this.selectedMonth, this.selectedYear)
          .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (res) => {
              // A disabled notification or a missing address is not an error, but it is NOT a send
              // either — say so rather than showing a success toast for an email nobody received.
              this.messageService.add(res?.sent === false
                ? {
                    severity: 'warn',
                    summary: 'Reminder Not Sent',
                    detail: res.message,
                    life: 8000
                  }
                : {
                    severity: 'success',
                    summary: 'Reminder Sent',
                    detail: `Timesheet reminder sent to ${row.employeeName}`
                  });
            },
            error: (err) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: err?.error?.message || 'Failed to send reminder email'
              });
            }
          });
      }
    });
  }

  submitTimesheet(row: TimesheetReportRow): void {
    this.confirmationService.confirm({
      message: `Submit this timesheet for approval? You won't be able to edit it after submitting.`,
      header: 'Confirm Submit',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-primary',
      accept: () => {
        const id = row.timesheet?.id;
        if (!id) return;
        this.timesheetService.submitTimesheet(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Timesheet submitted successfully' });
            this.loadReport();
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Failed to submit work log' });
          }
        });
      }
    });
  }

  approveTimesheet(row: TimesheetReportRow): void {
    this.confirmationService.confirm({
      message: `Approve this timesheet for "${row.employeeName}"?`,
      header: 'Confirm Approve',
      icon: 'pi pi-check-circle',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => {
        const id = row.timesheet?.id;
        if (!id) return;
        this.timesheetService.approveTimesheet(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Timesheet approved successfully' });
            this.loadReport();
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Failed to approve timesheet' });
          }
        });
      }
    });
  }

  revertToDraft(row: TimesheetReportRow): void {
    this.confirmationService.confirm({
      message: `Revert this timesheet back to draft? The employee will be able to edit it again.`,
      header: 'Confirm Revert',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        const id = row.timesheet?.id;
        if (!id) return;
        this.timesheetService.revertToDraft(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Timesheet reverted to draft' });
            this.loadReport();
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Failed to revert timesheet' });
          }
        });
      }
    });
  }

  viewTimesheet(row: TimesheetReportRow): void {
    this.router.navigate(['/timesheet'], {
      queryParams: {
        employeeId: row.employeeId,
        year: this.selectedYear,
        month: this.selectedMonth
      }
    });
  }

  getStatusLabel(row: TimesheetReportRow): string {
    if (row.isApproved) return 'Approved';
    if (row.isSubmitted) return 'Submitted';
    if (!row.timesheet?.id) return 'Not Entered';
    return 'Draft';
  }

  private getRowStatusKey(r: TimesheetReportRow): string {
    if (r.isApproved) return 'approved';
    if (r.isSubmitted) return 'submitted';
    if (r.timesheet?.id) return 'draft';
    return 'notEntered';
  }

  getStatusSeverity(row: TimesheetReportRow): 'success' | 'info' | 'secondary' {
    if (row.isApproved) return 'success';
    if (row.isSubmitted) return 'info';
    if (!row.timesheet?.id) return 'secondary';
    return 'secondary';
  }

  /** True when total timesheet is zero hours / zero minutes (highlight row in red). */
  isZeroTotalHours(row: TimesheetReportRow): boolean {
    return row.totalHours == null || row.totalHours < 1e-6;
  }

  /** Uses shared util so week totals match Enter Timesheet and Draft page. */
  private convertWeekHoursToDecimal(weekHours: number): number {
    return convertWeekHoursToDecimalUtil(weekHours);
  }

  /** Week (1-5) from date for the given work log month/year. */
  private calculateWeekFromDate(date: Date | string | undefined, month: Month, year: number): number {
    if (!date) return 0;
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return 0;
    if (d.getFullYear() !== year || (d.getMonth() + 1) !== month) return 0;
    const day = d.getDate();
    if (day >= 1 && day <= 7) return 1;
    if (day >= 8 && day <= 14) return 2;
    if (day >= 15 && day <= 21) return 3;
    if (day >= 22 && day <= 28) return 4;
    if (day >= 29 && day <= 31) return 5;
    return 0;
  }

  private hoursMinutesToDecimalHours(hoursMinutes: number): number {
    return hoursMinutesToDecimalHoursUtil(hoursMinutes);
  }

  /** Uses shared util so display matches Enter Timesheet and Draft page. */
  formatHoursMinutes(decimalHours: number): string {
    return formatHoursMinutesUtil(decimalHours);
  }

  /** Format row/grand total so it matches Enter Timesheet (rounds to nearest minute). */
  formatTotalHours(decimalHours: number): string {
    return formatTotalHoursUtil(decimalHours);
  }

  /** Formatted row total from week decimals (avoids off-by-one-minute display). */
  getFormattedRowTotal(row: TimesheetReportRow): string {
    return formatTotalHoursFromDecimalArray([
      row.week1Hours ?? 0,
      row.week2Hours ?? 0,
      row.week3Hours ?? 0,
      row.week4Hours ?? 0,
      row.week5Hours ?? 0
    ]);
  }

  private buildProjectSummary(timesheet: Timesheet): ProjectSummaryRow[] {
    return (this.selectedEmployeeRow?.expandedProjectRows || []) as ProjectSummaryRow[];
  }

  /** Page-level: download timesheet report summary (all employees, weekly hours + total + status) */
  exportReportSummaryToExcel(): void {
    if (!this.rows || this.rows.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No work log data to export'
      });
      return;
    }
    try {
      const hasSelectedProjects = (this.selectedProjectIds?.length ?? 0) > 0;
      const totalHoursLabel = hasSelectedProjects
        ? 'Total Hours (Selected Projects)'
        : 'Total Hours';
      const selectedRowsForExport = hasSelectedProjects
        ? this.rows.filter((row) => this.getExportTotalHours(row) > 0)
        : this.rows;

      if (selectedRowsForExport.length === 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'No Data',
          detail: 'No spent time found for the selected project(s)'
        });
        return;
      }

      const monthName = this.months.find((m) => m.value === this.selectedMonth)?.label || 'Month';

      // Summary week/total columns respect the project filter exactly as before (via
      // getExportWeekHours), while the two project sheets break those same hours out per project —
      // which is what the collapsed "Total Hours (Selected Projects)" column used to hide.
      const fileName = `Work_Log_Report_${monthName}_${this.selectedYear}_${this.formatDate(new Date())}.xlsx`;

      this.timesheetService.exportWorkLogsToExcel({
        periodLabel: `${monthName} ${this.selectedYear}`,
        fileNamePrefix: 'Work_Log_Report',
        filters: this.buildExportFilterContext(hasSelectedProjects, totalHoursLabel),
        employees: selectedRowsForExport.map((row) => {
          const weekTotals = this.getExportWeekHours(row);
          return {
            employeeName: row.employeeName || '',
            status: this.getStatusLabel(row),
            weeks: [
              weekTotals.week1Hours, weekTotals.week2Hours, weekTotals.week3Hours,
              weekTotals.week4Hours, weekTotals.week5Hours
            ],
            projects: this.getSelectedProjectRowsForExport(row).map((p) => ({
              projectId: p.projectId,
              projectName: p.projectName || '-',
              pmName: p.pmName || '-',
              weeks: [p.week1Hours, p.week2Hours, p.week3Hours, p.week4Hours, p.week5Hours]
            }))
          };
        })
      }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (blob) => {
          saveAs(blob, fileName);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Timesheet report exported to Excel successfully'
          });
        },
        error: (error) => {
          console.error('Error exporting timesheet report:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to export work log report to Excel'
          });
        }
      });
    } catch (error) {
      console.error('Error exporting timesheet report:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to export work log report to Excel'
      });
    }
  }

  /** Names the active filters so the exported file states which slice of data it is. */
  private buildExportFilterContext(
    hasSelectedProjects: boolean,
    totalHoursLabel: string
  ): { label: string; value: string }[] {
    const labelsFor = (
      selected: string[],
      options: { label: string; value: string }[]
    ): string => selected.map(v => options.find(o => o.value === v)?.label ?? v).join(', ');

    const context: { label: string; value: string }[] = [];
    context.push({
      label: 'Service Group',
      value: this.selectedServiceGroupIds?.length
        ? labelsFor(this.selectedServiceGroupIds, this.serviceGroupOptions)
        : 'All'
    });
    context.push({
      label: 'Project',
      value: hasSelectedProjects
        ? labelsFor(this.selectedProjectIds, this.projectFilterOptions)
        : 'All'
    });
    context.push({
      label: 'Status',
      value: this.selectedStatuses?.length ? labelsFor(this.selectedStatuses, this.statusOptions) : 'All'
    });
    context.push({
      label: 'Total Hours',
      value: this.selectedTotalHoursThresholds?.length
        ? labelsFor(this.selectedTotalHoursThresholds, this.totalHoursFilterOptions)
        : 'All'
    });
    if (this.filterEmployeeName?.trim()) {
      context.push({ label: 'Search', value: this.filterEmployeeName.trim() });
    }
    // Makes explicit that, when projects are filtered, the totals are of the selected projects only.
    context.push({ label: 'Totals Basis', value: totalHoursLabel });
    return context;
  }

  private getSelectedProjectRowsForExport(row: TimesheetReportRow): ProjectSummaryRow[] {
    if ((this.selectedProjectIds?.length ?? 0) === 0) {
      return row.expandedProjectRows || [];
    }
    const selected = new Set(this.selectedProjectIds);
    return (row.expandedProjectRows || []).filter((project) => selected.has(project.projectId));
  }

  private getExportWeekHours(row: TimesheetReportRow): {
    week1Hours: number;
    week2Hours: number;
    week3Hours: number;
    week4Hours: number;
    week5Hours: number;
  } {
    if ((this.selectedProjectIds?.length ?? 0) === 0) {
      return {
        week1Hours: row.week1Hours || 0,
        week2Hours: row.week2Hours || 0,
        week3Hours: row.week3Hours || 0,
        week4Hours: row.week4Hours || 0,
        week5Hours: row.week5Hours || 0
      };
    }
    const selectedProjectRows = this.getSelectedProjectRowsForExport(row);
    return {
      week1Hours: selectedProjectRows.reduce((sum, project) => sum + (project.week1Hours || 0), 0),
      week2Hours: selectedProjectRows.reduce((sum, project) => sum + (project.week2Hours || 0), 0),
      week3Hours: selectedProjectRows.reduce((sum, project) => sum + (project.week3Hours || 0), 0),
      week4Hours: selectedProjectRows.reduce((sum, project) => sum + (project.week4Hours || 0), 0),
      week5Hours: selectedProjectRows.reduce((sum, project) => sum + (project.week5Hours || 0), 0)
    };
  }

  private getExportTotalHours(row: TimesheetReportRow): number {
    if ((this.selectedProjectIds?.length ?? 0) > 0) {
      return this.getSelectedProjectRowsForExport(row)
        .reduce((sum, project) => sum + (project.totalHours || 0), 0);
    }
    return row.totalHours || 0;
  }

  /** Per-employee: download timesheet Excel with same structure as Enter Timesheet page (#, Week, Project, Task Type, Description, Date, Hours, Weekly Total Hours + merges + Total row) */
  exportEmployeeTimesheetToExcel(row: TimesheetReportRow): void {
    const entries = this.getEntriesForEmployeeExport(row);
    if (entries.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: (this.selectedProjectIds?.length ?? 0) > 0
          ? 'No spent time entries for selected project(s)'
          : 'No work log entries to export for this employee'
      });
      return;
    }
    try {
      const month = row.timesheet?.month ?? this.selectedMonth;
      const year = row.timesheet?.year ?? this.selectedYear;
      const getEntryWeek = (e: TimesheetEntry) => this.calculateWeekFromDate(e.date, month, year);
      const sorted = [...entries].sort((a, b) => {
        const aDate = a.date ? new Date(a.date).getTime() : 0;
        const bDate = b.date ? new Date(b.date).getTime() : 0;
        return aDate - bDate;
      });

      type ExportRow = { '#': number | string; 'Week': string; 'Project': string; 'Task Type': string; 'Description': string; 'Date': string; 'Hours': string; 'Weekly Total Hours': string };
      const exportData: ExportRow[] = [];
      const merges: Array<{ s: { r: number; c: number }; e: { r: number; c: number } }> = [];
      const mergeRange = (sr: number, sc: number, er: number, ec: number) => ({ s: { r: sr, c: sc }, e: { r: er, c: ec } });
      const WEEK_COL = 1;
      const WEEKLY_TOTAL_COL = 7;

      const getEntryWeekHoursMinutesValue = (entry: TimesheetEntry, weekNum: number): number => {
        if (entry.date != null && entry.hours != null && getEntryWeek(entry) === weekNum) return Number(entry.hours);
        if (entry.date == null) {
          const v = (entry as any)[`week${weekNum}Hours`];
          return typeof v === 'number' ? v : 0;
        }
        return 0;
      };

      let rowIndex = 0;
      let i = 0;
      while (i < sorted.length) {
        const e = sorted[i];
        const w = getEntryWeek(e);
        const weekStart = rowIndex;
        const weekEntries: TimesheetEntry[] = [];
        while (i < sorted.length && getEntryWeek(sorted[i]) === w) {
          weekEntries.push(sorted[i]);
          i++;
        }
        const weekValues = w > 0 ? weekEntries.map((entry) => getEntryWeekHoursMinutesValue(entry, w)).filter((v) => v > 0) : [];
        const sumHM = weekValues.length > 0 ? sumHoursMinutes(weekValues) : { h: 0, m: 0 };
        const weeklyTotalFormatted = w > 0 ? formatHoursMinutesFromHM(sumHM.h, sumHM.m) : '';
        const weekLabel = w > 0 ? `Week${w}` : '';
        weekEntries.forEach((entry, idx) => {
          const entryHours = entry.date != null && entry.hours != null
            ? formatHoursMinutesFromHM(parseHoursMinutes(Number(entry.hours)).h, parseHoursMinutes(Number(entry.hours)).m)
            : '';
          exportData.push({
            '#': rowIndex + 1,
            'Week': idx === 0 ? weekLabel : '',
            'Project': entry.projectName || '',
            'Task Type': entry.taskName || '',
            'Description': entry.description || '',
            'Date': entry.date ? new Date(entry.date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }) : '',
            'Hours': entryHours,
            'Weekly Total Hours': idx === 0 ? weeklyTotalFormatted : ''
          });
          rowIndex++;
        });
        if (weekEntries.length > 1 && w > 0) {
          merges.push(mergeRange(weekStart + 1, WEEK_COL, rowIndex, WEEK_COL));
          merges.push(mergeRange(weekStart + 1, WEEKLY_TOTAL_COL, rowIndex, WEEKLY_TOTAL_COL));
        }
      }

      const { totalHours: th, totalMinutes: tm } = getTimesheetTotalHoursMinutes(entries);
      const totalRowIndex = rowIndex;
      exportData.push({
        '#': 'Total',
        'Week': '',
        'Project': '',
        'Task Type': '',
        'Description': '',
        'Date': '',
        'Hours': '',
        'Weekly Total Hours': formatHoursMinutesFromHM(th, tm)
      });
      merges.push(mergeRange(totalRowIndex + 1, 0, totalRowIndex + 1, 6));

      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
      worksheet['!merges'] = merges;
      worksheet['!cols'] = [
        { wch: 4 }, { wch: 8 }, { wch: 38 }, { wch: 14 }, { wch: 24 }, { wch: 12 }, { wch: 10 }, { wch: 18 }
      ];
      const workbook: XLSX.WorkBook = {
        Sheets: { 'Timesheet': worksheet },
        SheetNames: ['Timesheet']
      };
      const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const monthName = this.months.find((m) => m.value === this.selectedMonth)?.label || 'Month';
      const safeName = (row.employeeName || row.employeeId || 'Employee').replace(/[^a-zA-Z0-9_-]/g, '_');
      const fileName = `Timesheet_${safeName}_${monthName}_${this.selectedYear}_${this.formatDate(new Date())}.xlsx`;
      saveAs(data, fileName);
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Timesheet exported to Excel successfully'
      });
    } catch (error) {
      console.error('Error exporting employee timesheet:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to export work log to Excel'
      });
    }
  }

  private getEntriesForEmployeeExport(row: TimesheetReportRow): TimesheetEntry[] {
    const entries = row.timesheet?.entries || [];
    if ((this.selectedProjectIds?.length ?? 0) === 0) {
      return entries;
    }
    const selectedProjectIds = new Set(this.selectedProjectIds);
    return entries.filter((entry) => !!entry.projectId && selectedProjectIds.has(entry.projectId));
  }

  private formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}${m}${d}`;
  }

  /** Format hours to display with 1-2 decimal places */
  formatHours(hours: number | undefined | null): string {
    if (hours === undefined || hours === null) return '-';
    return Number(hours.toFixed(2)).toString();
  }
}

